import { ref } from 'vue';
import { ElectronApi } from '@/services/electron.api';
import { getWebBuildTime } from '@/utils/version.utils';

export interface VersionHistoryItem {
  buildTime: string;
  path: string;
}

const VERSIONS_JSON_URL = (() => {
  const { origin, pathname } = window.location;
  // 始终取到 bucket 根目录，去掉 /index.html 或 /history/xxx.html
  const basePath = pathname
    .replace(/\/history\/[^/]+$/, '')
    .replace(/\/index\.html$/, '')
    .replace(/\/$/, '');
  return origin + basePath + '/versions.json';
})();

/** 获取 bucket 根路径（不随页面跳转变化） */
const getBasePath = () => {
  const { pathname } = window.location;
  return pathname
    .replace(/\/history\/[^/]+$/, '')
    .replace(/\/index\.html$/, '')
    .replace(/\/$/, '');
};

const LOCAL_STORAGE_KEY = 'version-history-cache';

/** 标记当前是否处于历史版本模式（切换历史版本后为 true，关闭窗口自动清除） */
export const isHistoryMode = ref(
  sessionStorage.getItem('version-history-mode') === 'true'
);

/**
 * 历史版本管理 hook
 * 仅在 staging 环境下可用
 */
export const useVersionHistory = () => {
  const versions = ref<VersionHistoryItem[]>([]);
  const loading = ref(false);
  const currentBuildTime = getWebBuildTime() ?? '';

  /**
   * 是否启用历史版本功能
   */
  const enabled = true;

  /**
   * 从 CDN 拉取版本列表，失败时降级到本地缓存
   */
  const fetchVersions = async () => {
    if (!enabled) return;
    loading.value = true;
    try {
      const res = await window.fetch(VERSIONS_JSON_URL, {
        cache: 'no-store'
      });
      if (res.status !== 200) throw new Error('versions.json 请求失败');
      const data: VersionHistoryItem[] = await res.json();
      // 仅保留上一个版本（不包含当前版本）
      versions.value = data
        .filter((item) => item.buildTime !== currentBuildTime)
        .slice(0, 1);
      // 缓存到本地
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(versions.value));
    } catch (error) {
      console.warn('拉取历史版本失败，使用本地缓存', error);
      // 降级到本地缓存
      try {
        const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (cached) {
          versions.value = JSON.parse(cached).slice(0, 1);
        }
      } catch {
        versions.value = [];
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * 切换到指定历史版本
   * 使用 window.location.href 跳转，确保 Electron preload 脚本重新注入
   */
  const switchToVersion = (version: VersionHistoryItem) => {
    const { origin } = window.location;
    const basePath = getBasePath();
    const targetUrl = origin + basePath + '/' + version.path;

    // 标记进入历史版本模式，阻止版本更新提醒（当次会话有效，关闭窗口自动清除）
    isHistoryMode.value = true;
    sessionStorage.setItem('version-history-mode', 'true');

    // 直接跳转，Electron 会重新注入 preload，所有 API 正常可用
    window.location.href = targetUrl;
  };

  return {
    enabled,
    versions,
    loading,
    currentBuildTime,
    fetchVersions,
    switchToVersion
  };
};
